import { create } from 'zustand'
import { track } from '@/lib/analytics'
import { type ApiResult, fallback, fetchWithTimeout, ok, unavailable } from '@/lib/api-result'
import { HEIGHT_MAX_M, HEIGHT_MIN_M } from '@/lib/clearance'
import { calculateFuelEstimate, sampleRoutePoints } from '@/lib/fuel-engine'
import { type GeneratorLine, GPH_MAX, GPH_MIN, generatorLine } from '@/lib/generator'
import { addRecentRoute } from '@/lib/recent-routes'
import { storageKeys } from '@/lib/storage-keys'
import type {
	CalculationWarnings,
	ElevationPoint,
	FuelEstimate,
	FuelType,
	StateFuelPrices,
	Vehicle,
	VehicleId,
	VehicleRestrictions,
	Waypoint,
} from '@/lib/types'
import { getVehicle } from '@/lib/vehicles'

/** Vehicle settings a share link carries (absent in the link = preset / off). */
export interface SharedSettings {
	customMpg: number | null
	customHeightM: number | null
	auxPowerOn: boolean
	customGph: number | null
	auxIdleHours: number
}

interface RouteState {
	// Input state
	origin: Waypoint | null
	destination: Waypoint | null
	waypoints: Waypoint[]
	vehicleId: VehicleId
	fuelType: FuelType
	customMpg: number | null
	customTankCapacity: number | null
	/** User's own vehicle height in meters (null = preset); bus/truck routing only */
	customHeightM: number | null
	/** Add generator/reefer fuel to the estimate (vehicles with auxPower only) */
	auxPowerOn: boolean
	/** User's own generator/reefer burn rate in gal/hr (null = preset) */
	customGph: number | null
	/** Extra generator/reefer hours while parked on this route (not saved) */
	auxIdleHours: number
	/**
	 * True when the current settings came from a share link. The mount-time
	 * localStorage hydration skips itself so the recipient sees exactly the
	 * sender's numbers, and nothing from the link is written to storage.
	 */
	fromShareLink: boolean

	// Result state
	estimate: FuelEstimate | null
	routeGeometry: GeoJSON.LineString | null
	isCalculating: boolean
	error: string | null
	/** ms elapsed on the most recent Calculate (used to display run time) */
	lastCalculationMs: number | null
	/**
	 * Always false in the current build. Retained on the type so existing
	 * consumers (CostSummary) keep their prop shape. The underlying shared
	 * route-level cache was removed 2026-04-18 (Codex Critical #2 — the
	 * client-computed hash key was trivially poisonable). Every Calculate
	 * now runs the full pipeline; per-layer caches (elevation Redis, fuel
	 * prices L1+L2, edge geocode) still apply, just no route-level cache.
	 */
	wasCached: boolean

	// Actions
	setOrigin: (w: Waypoint | null) => void
	setDestination: (w: Waypoint | null) => void
	addWaypoint: (w: Waypoint) => void
	removeWaypoint: (index: number) => void
	updateWaypoint: (index: number, w: Waypoint) => void
	reorderWaypoints: (from: number, to: number) => void
	importStops: (stops: Waypoint[]) => void
	setVehicle: (v: VehicleId) => void
	setFuelType: (f: FuelType) => void
	setCustomMpg: (mpg: number | null) => void
	setCustomTankCapacity: (gallons: number | null) => void
	setCustomHeightM: (meters: number | null) => void
	setAuxPowerOn: (on: boolean) => void
	setCustomGph: (gph: number | null) => void
	setAuxIdleHours: (hours: number) => void
	/** Apply a share link's vehicle settings for this session only (no storage writes). */
	applySharedSettings: (settings: SharedSettings) => void
	calculate: () => Promise<void>
	reset: () => void
}

/**
 * Custom-MPG persistence helpers — store per-vehicle overrides in localStorage
 * so tour managers don't have to re-enter their real MPG every visit. Scoped
 * per-vehicle so an override on a Prevost doesn't bleed into the Sprinter.
 *
 * Guarded against SSR (window undefined) and quota exceptions. localStorage
 * failures never block the app — we just lose persistence for that session.
 */
function loadSavedCustomMpg(vehicleId: VehicleId): number | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(storageKeys.customMpg(vehicleId))
		if (!raw) return null
		const parsed = Number.parseFloat(raw)
		return Number.isFinite(parsed) && parsed >= 1 && parsed <= 50 ? parsed : null
	} catch {
		return null
	}
}

function saveCustomMpg(vehicleId: VehicleId, mpg: number | null): void {
	if (typeof window === 'undefined') return
	try {
		const key = storageKeys.customMpg(vehicleId)
		if (mpg === null) {
			window.localStorage.removeItem(key)
		} else {
			window.localStorage.setItem(key, String(mpg))
		}
	} catch {
		// localStorage may be disabled or over quota — silently skip
	}
}

/**
 * Per-vehicle tank-capacity overrides — same persistence pattern as customMpg.
 * A user with an extended-range Prevost (300 gal saddle) shouldn't have to
 * re-enter that every visit, and their value shouldn't bleed into the Sprinter
 * preset when they switch vehicles.
 *
 * Range guard 5–500 gal: covers the realistic touring spectrum from a Smart
 * Car (~9 gal) to a custom triple-tank Prevost (~300 gal). 500 gal ceiling
 * stops absurd inputs from breaking the optimizer's lookahead window.
 */
function loadSavedCustomTank(vehicleId: VehicleId): number | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(storageKeys.customTank(vehicleId))
		if (!raw) return null
		const parsed = Number.parseFloat(raw)
		return Number.isFinite(parsed) && parsed >= 5 && parsed <= 500 ? parsed : null
	} catch {
		return null
	}
}

function saveCustomTank(vehicleId: VehicleId, gallons: number | null): void {
	if (typeof window === 'undefined') return
	try {
		const key = storageKeys.customTank(vehicleId)
		if (gallons === null) {
			window.localStorage.removeItem(key)
		} else {
			window.localStorage.setItem(key, String(gallons))
		}
	} catch {
		// localStorage may be disabled or over quota — silently skip
	}
}

/**
 * Per-vehicle height override for bridge-aware routing (meters). Same
 * persistence pattern as customMpg; range-checked with the same bounds the
 * directions API enforces.
 */
function loadSavedCustomHeight(vehicleId: VehicleId): number | null {
	if (typeof window === 'undefined') return null
	try {
		const raw = window.localStorage.getItem(storageKeys.customHeight(vehicleId))
		if (!raw) return null
		const parsed = Number.parseFloat(raw)
		return Number.isFinite(parsed) && parsed >= HEIGHT_MIN_M && parsed <= HEIGHT_MAX_M
			? parsed
			: null
	} catch {
		return null
	}
}

function saveCustomHeight(vehicleId: VehicleId, meters: number | null): void {
	if (typeof window === 'undefined') return
	try {
		const key = storageKeys.customHeight(vehicleId)
		if (meters === null) {
			window.localStorage.removeItem(key)
		} else {
			window.localStorage.setItem(key, String(meters))
		}
	} catch {
		// localStorage may be disabled or over quota — silently skip
	}
}

/** Per-vehicle generator/reefer settings — same persistence pattern as MPG. */
export function loadSavedAuxPower(vehicleId: VehicleId): { on: boolean; gph: number | null } {
	if (typeof window === 'undefined') return { on: false, gph: null }
	try {
		const on = window.localStorage.getItem(storageKeys.auxPowerOn(vehicleId)) === '1'
		const raw = window.localStorage.getItem(storageKeys.customGph(vehicleId))
		const parsed = raw === null ? Number.NaN : Number.parseFloat(raw)
		const gph = Number.isFinite(parsed) && parsed >= GPH_MIN && parsed <= GPH_MAX ? parsed : null
		return { on, gph }
	} catch {
		return { on: false, gph: null }
	}
}

function saveAuxPower(vehicleId: VehicleId, on: boolean, gph: number | null): void {
	if (typeof window === 'undefined') return
	try {
		const onKey = storageKeys.auxPowerOn(vehicleId)
		const gphKey = storageKeys.customGph(vehicleId)
		if (on) window.localStorage.setItem(onKey, '1')
		else window.localStorage.removeItem(onKey)
		if (gph === null) window.localStorage.removeItem(gphKey)
		else window.localStorage.setItem(gphKey, String(gph))
	} catch {
		// localStorage may be disabled or over quota — silently skip
	}
}

/** Body for /api/directions — heightM only when the user set one. */
function directionsBody(
	coordinates: [number, number][],
	vehicleId: VehicleId,
	heightM: number | null,
): string {
	return JSON.stringify(
		heightM === null ? { coordinates, vehicleId } : { coordinates, vehicleId, heightM },
	)
}

/** Normalized directions payload the pipeline actually consumes */
interface DirectionsData {
	geometry: GeoJSON.LineString
	distanceMeters: number
	durationSeconds: number
	vehicleRestrictions: VehicleRestrictions
}

/**
 * Shown when Mapbox rejects a route for exceeding its per-request distance
 * ceiling. Surfaced by both the live preview (the moment a stop is dropped)
 * and the Calculate pipeline.
 */
const ROUTE_TOO_LONG_MESSAGE = 'This route is too long to calculate — try removing a stop.'

/**
 * Fetch route directions. Directions is a HARD dependency — no route means no
 * estimate — so failure returns an `unavailable` result and the pipeline
 * surfaces it as the store's `error` state (not a warning banner).
 */
async function fetchDirections(
	coords: [number, number][],
	vehicleId: VehicleId,
	heightM: number | null,
): Promise<ApiResult<DirectionsData>> {
	try {
		const res = await fetchWithTimeout('/api/directions', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: directionsBody(coords, vehicleId, heightM),
		})
		if (!res.ok) {
			// Translate the common hard failures into plain language. The usual
			// 422 on aggressive multi-stop routes is Mapbox's per-request distance
			// ceiling ("Route exceeds maximum distance limitation"), which a bare
			// status code does nothing to explain.
			let reason = 'The route service could not calculate this route. Please try again.'
			try {
				const body = (await res.json()) as { error?: string }
				if (res.status === 422 && /maximum distance|exceeds/i.test(body.error ?? '')) {
					reason = ROUTE_TOO_LONG_MESSAGE
				}
			} catch {
				// Non-JSON error body — keep the generic reason.
			}
			return unavailable(reason)
		}
		const data = await res.json()
		if (!data.geometry || typeof data.distance !== 'number') {
			return unavailable('Route service returned an empty or malformed response')
		}
		return ok({
			geometry: data.geometry as GeoJSON.LineString,
			distanceMeters: data.distance as number,
			durationSeconds: data.duration as number,
			vehicleRestrictions: (data.vehicleRestrictions as VehicleRestrictions | undefined) ?? 'none',
		})
	} catch (err) {
		const reason = err instanceof Error ? err.message : 'Route service unreachable'
		return unavailable(reason)
	}
}

/**
 * Fetch elevation data for sample points.
 *
 * The server picks a source (Open-Meteo primary, Mapbox fallback) and returns
 * which one it used via the `source` field. If the fallback path ran, we
 * surface that as an `ApiResult.fallback` so the warnings banner can tell
 * the user "precision is reduced, backup source used."
 *
 * Soft dependency — on total failure, the pipeline proceeds without grade
 * adjustment and raises an `elevationStatus: 'unavailable'` warning.
 */
async function fetchElevations(
	points: Array<{ lng: number; lat: number }>,
): Promise<ApiResult<number[]>> {
	try {
		const res = await fetchWithTimeout(
			'/api/elevation',
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ coordinates: points }),
			},
			// Client must outlast the server's worst-case retry budget
			// (~20s Open-Meteo retries + ~15s Mapbox fallback = 35s) so
			// the user always receives the fallback's result rather than
			// an aborted request. 5s buffer keeps us below 45s perceived
			// wait, which is still acceptable for a long-route calc.
			// Codex review High #5, 2026-04-18.
			40_000,
		)
		if (!res.ok) {
			return unavailable(`Elevation service returned ${res.status}`)
		}
		const data = await res.json()
		const elevations = data.elevations as number[] | undefined
		if (!Array.isArray(elevations) || elevations.length !== points.length) {
			return unavailable('Elevation service returned malformed data')
		}
		// The server populates `source: 'open-meteo' | 'mapbox-fallback'` based on
		// which provider actually answered. Map backup-source responses to a
		// fallback ApiResult so the UI can flag degraded precision.
		if (data.source === 'mapbox-fallback') {
			const reason =
				typeof data.primaryReason === 'string' && data.primaryReason.length > 0
					? `Primary elevation source unavailable (${data.primaryReason}) — using backup (reduced precision)`
					: 'Primary elevation source unavailable — using backup (reduced precision)'
			return fallback(elevations, reason)
		}
		return ok(elevations)
	} catch (err) {
		const reason = err instanceof Error ? err.message : 'Elevation service unreachable'
		return unavailable(reason)
	}
}

/** Fuel prices payload — includes the timestamp of the underlying EIA fetch */
interface FuelPricesData {
	prices: StateFuelPrices
	/** ms-epoch — when EIA was last successfully queried (not when we received this response) */
	fetchedAt: number
}

/**
 * Fetch fuel prices. The server caches EIA for 24h; on upstream failure it
 * serves stale cache (still useful) or falls back to national average (warn).
 *
 * Return cases:
 * - `ok(data, 'fresh')` — fresh EIA fetch
 * - `ok(data, 'cached')` — from the server's 24h cache
 * - `fallback(data)` — server couldn't reach EIA, serving stale cache or national avg
 * - `unavailable()` — server didn't respond at all
 */
async function fetchFuelPrices(): Promise<ApiResult<FuelPricesData>> {
	try {
		// EIA cold fill can take several seconds; give it a generous ceiling.
		// On warm-cache paths the server responds in <100ms so this is harmless.
		const res = await fetchWithTimeout('/api/fuel-prices', {}, 20000)
		if (!res.ok) {
			return unavailable(`Price service returned ${res.status}`)
		}
		const data = await res.json()
		const prices = (data.prices as StateFuelPrices) ?? {}
		const fetchedAt = typeof data.fetchedAt === 'number' ? data.fetchedAt : Date.now()
		if (data.fallback === true || data.stale === true) {
			const reason =
				typeof data.reason === 'string' ? data.reason : 'EIA unavailable — using national average'
			return fallback({ prices, fetchedAt }, reason)
		}
		return ok({ prices, fetchedAt }, data.cached === true ? 'cached' : 'fresh')
	} catch (err) {
		const reason = err instanceof Error ? err.message : 'Price service unreachable'
		return unavailable(reason)
	}
}

/** Build elevation profile from sample points and elevation data (1:1 mapping) */
function buildElevationProfile(
	samplePoints: Array<{ lng: number; lat: number; distanceMiles: number }>,
	elevations: number[],
): ElevationPoint[] {
	return samplePoints.map((p, i) => ({
		distance: p.distanceMiles,
		elevation: elevations[i] ?? 0,
		lng: p.lng,
		lat: p.lat,
	}))
}

/**
 * Densify an elevation profile onto a finer sample grid by linear interpolation.
 *
 * We sample elevation at an adaptive rate (5-25 mi) to control Open-Meteo
 * and Mapbox upstream load, but we sample STATE at a dense 5mi rate so thin-
 * state corridors (DE on I-95, MD panhandle on I-68) aren't skipped. The
 * fuel engine expects elevation-per-point aligned to state-per-point, so we
 * interpolate sparse elevation samples onto the dense state-sample grid.
 *
 * Interpolation is purely linear between neighboring sparse samples.
 * Elevation typically varies slowly over 20mi stretches (the adaptive cap),
 * so linear interp is accurate to within a few feet of what a direct
 * measurement would return — well below the noise floor of raster DEM data.
 *
 * Called only when the two grids differ (i.e., on routes where the adaptive
 * elevation interval != 5mi — that's every route >500mi under current tuning).
 * When they match, caller can pass the sparse profile through unchanged.
 */
function densifyElevationProfile(
	sparseProfile: ElevationPoint[],
	densePoints: Array<{ lng: number; lat: number; distanceMiles: number }>,
): ElevationPoint[] {
	if (sparseProfile.length === 0) {
		return densePoints.map((p) => ({
			distance: p.distanceMiles,
			elevation: 0,
			lng: p.lng,
			lat: p.lat,
		}))
	}
	if (sparseProfile.length === 1) {
		const only = sparseProfile[0]
		const onlyElevation = only?.elevation ?? 0
		return densePoints.map((p) => ({
			distance: p.distanceMiles,
			elevation: onlyElevation,
			lng: p.lng,
			lat: p.lat,
		}))
	}

	// Two-pointer sweep through sparse samples as we walk dense points in order.
	let idx = 0
	return densePoints.map((p) => {
		const targetMile = p.distanceMiles
		// Advance until sparse[idx+1] is past the target (or we hit the end).
		while (idx < sparseProfile.length - 2 && (sparseProfile[idx + 1]?.distance ?? 0) < targetMile) {
			idx++
		}
		const a = sparseProfile[idx] as ElevationPoint
		const b = (sparseProfile[idx + 1] ?? a) as ElevationPoint
		const span = b.distance - a.distance
		const t = span > 0 ? (targetMile - a.distance) / span : 0
		const clampedT = Math.max(0, Math.min(1, t))
		const elevation = a.elevation + (b.elevation - a.elevation) * clampedT
		return {
			distance: targetMile,
			elevation: Math.round(elevation),
			lng: p.lng,
			lat: p.lat,
		}
	})
}

/** Assemble the final FuelEstimate from computed data */
function assembleFuelEstimate(
	stateBreakdown: ReturnType<typeof calculateFuelEstimate>,
	totalDistanceMiles: number,
	totalDurationSeconds: number,
	baseMpg: number,
	presetMpg: number,
	fuelType: FuelType,
	vehicleName: string,
	elevationProfile: ElevationPoint[],
	geometry: GeoJSON.LineString,
	warnings: CalculationWarnings,
): FuelEstimate {
	const totalGallons = stateBreakdown.reduce((sum, s) => sum + s.gallons, 0)
	const totalCost = stateBreakdown.reduce((sum, s) => sum + s.cost, 0)
	const averageEffectiveMpg =
		totalGallons > 0 ? Math.round((totalDistanceMiles / totalGallons) * 10) / 10 : baseMpg

	return {
		totalDistanceMiles: Math.round(totalDistanceMiles * 10) / 10,
		totalDurationMinutes: Math.round(totalDurationSeconds / 60),
		totalGallons: Math.round(totalGallons * 10) / 10,
		totalCost: Math.round(totalCost * 100) / 100,
		averageEffectiveMpg,
		baseMpg,
		presetMpg,
		fuelType,
		vehicleName,
		stateBreakdown,
		elevationProfile,
		routeGeometry: geometry,
		warnings,
	}
}

/** Per-source warning components — each stays small and single-purpose. */
interface PriceWarningPart {
	status: CalculationWarnings['fuelPricesStatus']
	reason: string | null
	ageMs: number
	fallbackStates: string[]
}

/** Derive the price-side warning state from the fetch result + engine output. */
function derivePriceWarning(
	priceResult: ApiResult<FuelPricesData>,
	stateBreakdown: ReturnType<typeof calculateFuelEstimate>,
): PriceWarningPart {
	if (!priceResult.ok) {
		return {
			status: 'unavailable',
			reason: priceResult.reason,
			ageMs: 0,
			fallbackStates: stateBreakdown.map((s) => s.stateCode),
		}
	}
	const ageMs = Date.now() - priceResult.data.fetchedAt
	if (priceResult.source === 'fallback') {
		return {
			status: 'fallback',
			reason: priceResult.reason,
			ageMs,
			fallbackStates: stateBreakdown.map((s) => s.stateCode),
		}
	}
	// Healthy response. The server returns PADD regional averages for every
	// state in STATE_TO_PADD (all 50 + DC), so a missing state here would mean
	// EIA has a catastrophic gap (shouldn't happen, kept for defense in depth).
	// We exclude the 'US' bucket — that's unattributed-miles (off-map coords),
	// surfaced via the state-detection banner instead.
	const missing = stateBreakdown
		.filter((s) => s.stateCode !== 'US' && !priceResult.data.prices[s.stateCode])
		.map((s) => s.stateCode)
	return {
		status: priceResult.source,
		reason: null,
		ageMs,
		fallbackStates: missing,
	}
}

/**
 * Threshold below which unattributed-mile segments are treated as rounding
 * noise rather than a genuine accuracy concern. Coastal routes often clip a
 * mile or two over water at the edge of polygon boundaries — not worth a
 * banner for.
 */
const UNATTRIBUTED_MILES_BANNER_THRESHOLD = 5

/**
 * Bucket route distance into coarse ranges for analytics. Keeps Plausible's
 * prop cardinality low (7 buckets) while still giving us useful patterns
 * ("most routes are in the 200-500 mi range").
 */
function bucketMiles(miles: number): string {
	if (miles < 100) return '<100'
	if (miles < 250) return '100-250'
	if (miles < 500) return '250-500'
	if (miles < 1000) return '500-1000'
	if (miles < 2000) return '1000-2000'
	if (miles < 3000) return '2000-3000'
	return '3000+'
}

/**
 * Derive the state-detection warning from the result + engine output.
 *
 * In the polygon-based pipeline, the HTTP call typically succeeds fully
 * (it's an offline computation). The interesting case is when some individual
 * coords returned null — those get bucketed as 'US' and priced at the
 * national-average fallback. If the total 'US' miles exceeds our noise
 * threshold, we flag it as degraded so the user knows their estimate has
 * some miles attributed to the wrong tier of pricing.
 */
function deriveStateWarning(
	stateResult: ApiResult<string[]>,
	stateBreakdown: ReturnType<typeof calculateFuelEstimate>,
): {
	status: CalculationWarnings['stateDetectionStatus']
	degradedMiles: number
} {
	if (!stateResult.ok) {
		return {
			status: 'unavailable',
			degradedMiles: stateBreakdown.reduce((sum, s) => sum + s.distanceMiles, 0),
		}
	}
	// Even on a successful HTTP call, individual coords may have been null
	// (off-map — coastal water, outside US boundaries). We coerce those to 'US'
	// in the pipeline and price them at national average. Surface as degraded
	// if the total is non-trivial.
	const unattributedMiles = stateBreakdown
		.filter((s) => s.stateCode === 'US')
		.reduce((sum, s) => sum + s.distanceMiles, 0)
	if (unattributedMiles > UNATTRIBUTED_MILES_BANNER_THRESHOLD) {
		return { status: 'degraded', degradedMiles: unattributedMiles }
	}
	return { status: 'ok', degradedMiles: 0 }
}

/**
 * Build the `CalculationWarnings` object from the four per-source results.
 *
 * Fallback-state detection: we know a state "fell back to national average"
 * when the EIA response was fresh/cached but that state code wasn't in the
 * response. We cross-reference after the state breakdown is computed.
 */
function buildWarnings(
	elevResult: ApiResult<number[]>,
	stateResult: ApiResult<string[]>,
	priceResult: ApiResult<FuelPricesData>,
	stateBreakdown: ReturnType<typeof calculateFuelEstimate>,
	vehicleRestrictions: VehicleRestrictions,
): CalculationWarnings {
	const price = derivePriceWarning(priceResult, stateBreakdown)
	const state = deriveStateWarning(stateResult, stateBreakdown)

	// Elevation: distinguish `unavailable` (both sources failed) from
	// `degraded` (primary failed, backup succeeded with reduced precision)
	let elevationStatus: CalculationWarnings['elevationStatus']
	let elevationReason: string | null
	if (!elevResult.ok) {
		elevationStatus = 'unavailable'
		elevationReason = elevResult.reason
	} else if (elevResult.source === 'fallback') {
		elevationStatus = 'degraded'
		elevationReason = elevResult.reason
	} else {
		elevationStatus = 'ok'
		elevationReason = null
	}

	return {
		vehicleRestrictions,
		fuelPricesStatus: price.status,
		fuelPricesFallbackStates: price.fallbackStates,
		fuelPricesAgeMs: price.ageMs,
		fuelPricesReason: price.reason,
		elevationStatus,
		elevationReason,
		stateDetectionStatus: state.status,
		stateDetectionDegradedMiles: state.degradedMiles,
	}
}

/** Shortest route we'll estimate (~160 m); below this the trip is effectively zero. */
const MIN_ROUTE_MILES = 0.1

/**
 * Full calculation pipeline: directions → elevation → states → prices → estimate.
 *
 * Failure contract:
 * - Directions failure → throw (no route = no estimate — hard fail surfaced as
 *   `error` in the store).
 * - Elevation / state detection / fuel price failures → proceed with degraded
 *   data and emit warnings, so the user sees an honest answer instead of a
 *   silent wrong one.
 */
async function runFuelPipeline(
	origin: Waypoint,
	destination: Waypoint,
	waypoints: Waypoint[],
	vehicle: Vehicle,
	baseMpg: number,
	fuelType: FuelType,
	heightM: number | null,
): Promise<FuelEstimate> {
	const allCoords: [number, number][] = [
		[origin.lng, origin.lat],
		...waypoints.map((w) => [w.lng, w.lat] as [number, number]),
		[destination.lng, destination.lat],
	]

	const dirResult = await fetchDirections(allCoords, vehicle.id, heightM)
	if (!dirResult.ok) {
		throw new Error(dirResult.reason)
	}
	const { geometry, distanceMeters, durationSeconds, vehicleRestrictions } = dirResult.data
	const totalDistanceMiles = distanceMeters * 0.000621371
	// Origin = destination (or a few meters apart): Mapbox returns distance 0,
	// and the sampling math divides by it — the estimate rendered "$NaN".
	if (!(totalDistanceMiles >= MIN_ROUTE_MILES)) {
		throw new Error('Origin and destination are the same place — pick two different stops')
	}

	// Two sample grids with different purposes:
	//
	//   elevationPoints — adaptive (5-25mi) based on route length. Rate-
	//     limited upstream budget means we can't blast coast-to-coast with
	//     663 coords; adaptive sampling keeps us under Open-Meteo + Mapbox
	//     burst limits.
	//
	//   statePoints — ALWAYS 5mi regardless of route length. State detection
	//     is an offline point-in-polygon lookup (zero network cost), so the
	//     dense grid is effectively free, and it ensures thin-state corridors
	//     like DE on I-95 or MD panhandle on I-68 never fall between samples.
	//     Codex 2026-04-19 medium — before this, adaptive sampling skipped
	//     states narrower than the interval on long routes, mispricing a few
	//     miles as the neighboring state.
	//
	// Elevation at the denser state-grid resolution is linearly interpolated
	// from the sparse elevation samples via `densifyElevationProfile`. For
	// grade-adjusted fuel math, this is accurate to within a few feet per
	// dense point — well below the raster DEM noise floor we're already on.
	const STATE_DETECTION_INTERVAL_MILES = 5
	const elevationPoints = sampleRoutePoints(geometry, totalDistanceMiles)
	const statePoints = sampleRoutePoints(
		geometry,
		totalDistanceMiles,
		STATE_DETECTION_INTERVAL_MILES,
	)

	const [elevResult, stateResult, priceResult] = await Promise.all([
		fetchElevations(elevationPoints.map((p) => ({ lng: p.lng, lat: p.lat }))),
		determineStates(statePoints),
		fetchFuelPrices(),
	])

	// Degraded-mode fallbacks: use zeros for elevation (no grade adjustment),
	// 'US' for unknown state (national avg price), and {} for unknown prices
	// (engine falls back to NATIONAL_AVERAGE_PRICES). Each of these is
	// tracked in `warnings` so the UI can tell the user what happened.
	const elevations = elevResult.ok ? elevResult.data : elevationPoints.map(() => 0)
	const stateAtPoints = stateResult.ok ? stateResult.data : statePoints.map(() => 'US')
	const fuelPrices = priceResult.ok ? priceResult.data.prices : {}

	// Sparse elevation profile (at adaptive intervals) is the *truth* — we
	// keep it around for the ElevationChart UI and share-URL consumers. The
	// engine receives a DENSIFIED version aligned to the state grid so its
	// per-segment loop attributes miles to the correct state even when states
	// are narrower than the elevation interval.
	const sparseElevationProfile = buildElevationProfile(elevationPoints, elevations)
	const denseElevationProfile = densifyElevationProfile(sparseElevationProfile, statePoints)

	const stateBreakdown = calculateFuelEstimate(
		denseElevationProfile,
		stateAtPoints,
		baseMpg,
		vehicle.weightClass,
		fuelType,
		fuelPrices,
	)

	const warnings = buildWarnings(
		elevResult,
		stateResult,
		priceResult,
		stateBreakdown,
		vehicleRestrictions,
	)

	// Return the SPARSE elevation profile to downstream consumers (chart
	// component, map markers, share URL). No need to pass the dense one up —
	// it's an internal detail of the fuel calculation.
	return assembleFuelEstimate(
		stateBreakdown,
		totalDistanceMiles,
		durationSeconds,
		baseMpg,
		vehicle.baseMpg,
		fuelType,
		vehicle.name,
		sparseElevationProfile,
		geometry,
		warnings,
	)
}

/** Abort controller for in-flight route preview requests */
let previewAbort: AbortController | null = null

/**
 * Lightweight route preview — fetches only the Directions geometry to draw the
 * route line on the map. No elevation, no state detection, no fuel prices.
 * Fires automatically when origin/destination/waypoints change.
 */
async function fetchRoutePreview(
	get: () => RouteState,
	set: (partial: Partial<RouteState>) => void,
) {
	// Cancel any in-flight preview request
	if (previewAbort) previewAbort.abort()

	const { origin, destination, waypoints, vehicleId, customHeightM } = get()
	if (!origin || !destination) {
		set({ routeGeometry: null, error: null })
		return
	}

	// Preview is going to proceed — clear any stale error (e.g. a prior
	// too-long banner) so a transient failure or a recovery (user removed the
	// offending stop) doesn't leave it stuck. The 422 branch below is the only
	// place that re-sets the too-long message.
	set({ error: null })

	const allCoords: [number, number][] = [
		[origin.lng, origin.lat],
		...waypoints.map((w) => [w.lng, w.lat] as [number, number]),
		[destination.lng, destination.lat],
	]

	previewAbort = new AbortController()

	try {
		const res = await fetch('/api/directions', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: directionsBody(allCoords, vehicleId, customHeightM),
			signal: previewAbort.signal,
		})
		if (!res.ok) {
			// The preview is the FIRST place a too-long route shows up (it fires on
			// every stop change, before Calculate), so surface that case right away
			// for drop-time feedback. Other transient preview failures stay silent —
			// Calculate reports them properly.
			if (res.status === 422) {
				try {
					const body = (await res.json()) as { error?: string }
					if (/maximum distance|exceeds/i.test(body.error ?? '')) {
						set({ error: ROUTE_TOO_LONG_MESSAGE })
					}
				} catch {
					// non-JSON error body — ignore
				}
			}
			return
		}
		const data = await res.json()
		set({ routeGeometry: data.geometry as GeoJSON.LineString, error: null })
	} catch {
		// Aborted or network error — ignore silently
	}
}

/**
 * Analytics for a fresh Calculate. Origin/dest state codes come from the first
 * and last segments of the breakdown — gives us corridor data (e.g.,
 * "TN -> CO") without PII.
 */
function trackCalculated(
	estimate: FuelEstimate,
	inputs: Pick<RouteState, 'vehicleId' | 'fuelType' | 'customMpg' | 'waypoints'>,
): void {
	const origState = estimate.stateBreakdown[0]?.stateCode ?? 'unknown'
	const destState =
		estimate.stateBreakdown[estimate.stateBreakdown.length - 1]?.stateCode ?? 'unknown'
	track('Calculated', {
		vehicle: inputs.vehicleId,
		fuel_type: inputs.fuelType,
		overrode_mpg: inputs.customMpg !== null,
		waypoint_count: inputs.waypoints.length,
		source: 'fresh',
		origin_state: origState,
		dest_state: destState,
		distance_miles_bucket: bucketMiles(estimate.totalDistanceMiles),
	})
}

/**
 * Monotonic id of the latest Calculate run. `reset()` bumps it too, so any
 * run still in flight can tell it has been superseded.
 */
let calcGeneration = 0

/** The inputs a Calculate run was computed from — route, vehicle, and fuel math. */
type CalcInputs = Pick<
	RouteState,
	'origin' | 'destination' | 'waypoints' | 'vehicleId' | 'customHeightM' | 'fuelType' | 'customMpg'
>

/**
 * True when a finished Calculate run may still write its result: no newer run
 * (or reset) started, and none of the inputs it read have changed. WHY: the
 * pipeline takes seconds — without this, clicking Reset or editing a stop mid-
 * calculation let the old estimate land on top of the new inputs. Reference
 * equality is exact here: every route setter replaces these values.
 */
function isCalcCurrent(generation: number, inputs: CalcInputs, now: RouteState): boolean {
	return (
		generation === calcGeneration &&
		now.origin === inputs.origin &&
		now.destination === inputs.destination &&
		now.waypoints === inputs.waypoints &&
		now.vehicleId === inputs.vehicleId &&
		now.customHeightM === inputs.customHeightM &&
		now.fuelType === inputs.fuelType &&
		now.customMpg === inputs.customMpg
	)
}

/**
 * `{ estimate: null }` when `next` changes any input the finished estimate was
 * computed from, else `{}`. WHY: a completed estimate must never outlive its
 * inputs — switching Diesel → Gas used to leave the Diesel total on screen
 * (and shareable/exportable) until the user recalculated. No-op writes (mount
 * rehydration, a share link re-applying the same value) keep the estimate.
 */
function invalidateEstimate(now: RouteState, next: Partial<CalcInputs>): { estimate?: null } {
	const keys = Object.keys(next) as (keyof CalcInputs)[]
	return keys.some((key) => next[key] !== now[key]) ? { estimate: null } : {}
}

export const useRouteStore = create<RouteState>((set, get) => ({
	origin: null,
	destination: null,
	waypoints: [],
	vehicleId: 'nightliner',
	fuelType: 'diesel',
	// ALWAYS start with customMpg = null. Reading localStorage here would
	// make the server and client disagree on first render (server has no
	// localStorage, client does) — which triggers a React hydration mismatch
	// now that VehiclePicker's selected card reflects the custom value.
	//
	// Saved values are rehydrated from localStorage in FuelEstimator's
	// mount effect (client-only) and on every setVehicle call. That's a
	// one-frame flash of "preset" before the custom value loads, which is
	// invisible in practice and avoids the SSR mismatch entirely.
	customMpg: null,
	// Same SSR-safe init as customMpg — rehydrated client-side on mount.
	customTankCapacity: null,
	customHeightM: null,
	auxPowerOn: false,
	customGph: null,
	auxIdleHours: 0,
	fromShareLink: false,

	estimate: null,
	routeGeometry: null,
	isCalculating: false,
	error: null,
	lastCalculationMs: null,
	wasCached: false,

	setOrigin: (w) => {
		set({ origin: w, estimate: null })
		fetchRoutePreview(get, set)
	},
	setDestination: (w) => {
		set({ destination: w, estimate: null })
		fetchRoutePreview(get, set)
	},

	addWaypoint: (w) => {
		set((s) => ({ waypoints: [...s.waypoints, w], estimate: null }))
		fetchRoutePreview(get, set)
	},

	removeWaypoint: (index) => {
		set((s) => ({
			waypoints: s.waypoints.filter((_, i) => i !== index),
			estimate: null,
		}))
		fetchRoutePreview(get, set)
	},

	updateWaypoint: (index, w) => {
		set((s) => {
			const updated = [...s.waypoints]
			updated[index] = w
			return { waypoints: updated, estimate: null }
		})
		fetchRoutePreview(get, set)
	},

	reorderWaypoints: (from, to) => {
		set((s) => {
			const updated = [...s.waypoints]
			const [item] = updated.splice(from, 1) as [Waypoint]
			updated.splice(to, 0, item)
			return { waypoints: updated, estimate: null }
		})
		fetchRoutePreview(get, set)
	},

	// Atomic multi-stop import (file upload / pasted itinerary). Replaces the
	// entire route in ONE set() — origin = first stop, destination = last stop,
	// everything between = waypoints — then fires the preview EXACTLY ONCE.
	// Looping the per-stop setters instead would trigger N redundant preview
	// fetches (each aborting the last) and N store renders; this is one of each.
	importStops: (stops) => {
		if (stops.length < 2) {
			set({ error: 'Import needs at least an origin and a destination' })
			return
		}
		const origin = stops[0]
		const destination = stops[stops.length - 1]
		const waypoints = stops.slice(1, -1)
		set({ origin, destination, waypoints, estimate: null, error: null, routeGeometry: null })
		fetchRoutePreview(get, set)
	},

	setVehicle: (v) => {
		const vehicle = getVehicle(v)
		// Restore any saved overrides for this vehicle — each vehicle has its
		// own remembered MPG and tank capacity (a user's real Prevost MPG and
		// extended-range tank are independent of their Sprinter setup).
		const savedMpg = loadSavedCustomMpg(v)
		const savedTank = loadSavedCustomTank(v)
		const savedHeight = loadSavedCustomHeight(v)
		const savedAux = loadSavedAuxPower(v)
		const inputs = {
			vehicleId: v,
			fuelType: vehicle.defaultFuel,
			customMpg: savedMpg,
			customHeightM: savedHeight,
		}
		set({
			...inputs,
			...invalidateEstimate(get(), inputs),
			customTankCapacity: savedTank,
			auxPowerOn: savedAux.on,
			customGph: savedAux.gph,
		})
		// Routing is vehicle-aware (bridge height / weight limits), so a new
		// vehicle can mean a different route line.
		fetchRoutePreview(get, set)
	},

	setFuelType: (f) => set((s) => ({ fuelType: f, ...invalidateEstimate(s, { fuelType: f }) })),
	setCustomMpg: (mpg) => {
		// Persist under the current vehicle — so switching vehicles doesn't clobber
		// the prior vehicle's saved MPG
		const { vehicleId } = get()
		saveCustomMpg(vehicleId, mpg)
		set((s) => ({ customMpg: mpg, ...invalidateEstimate(s, { customMpg: mpg }) }))
	},
	setCustomTankCapacity: (gallons) => {
		const { vehicleId } = get()
		saveCustomTank(vehicleId, gallons)
		set({ customTankCapacity: gallons })
	},
	// Generator/reefer fuel is derived from the finished estimate at render time
	// (see selectAuxPowerLine), so flipping these never re-runs the route.
	setAuxPowerOn: (on) => {
		const { vehicleId, customGph } = get()
		saveAuxPower(vehicleId, on, customGph)
		set({ auxPowerOn: on })
	},
	setCustomGph: (gph) => {
		const { vehicleId, auxPowerOn } = get()
		saveAuxPower(vehicleId, auxPowerOn, gph)
		set({ customGph: gph })
	},
	setAuxIdleHours: (hours) => set({ auxIdleHours: hours }),
	applySharedSettings: (settings) => {
		set((s) => ({
			...settings,
			...invalidateEstimate(s, {
				customMpg: settings.customMpg,
				customHeightM: settings.customHeightM,
			}),
			fromShareLink: true,
		}))
		// Height changes routing; everything else is derived at render time.
		fetchRoutePreview(get, set)
	},
	setCustomHeightM: (meters) => {
		const { vehicleId } = get()
		saveCustomHeight(vehicleId, meters)
		set((s) => ({ customHeightM: meters, ...invalidateEstimate(s, { customHeightM: meters }) }))
		// Height changes which bridges the route may use — redraw the preview.
		fetchRoutePreview(get, set)
	},

	calculate: async () => {
		const { origin, destination, waypoints, vehicleId, fuelType, customMpg, customHeightM } = get()

		if (!origin || !destination) {
			set({ error: 'Set both origin and destination' })
			return
		}

		const startMs = performance.now()
		const generation = ++calcGeneration
		const inputs: CalcInputs = {
			origin,
			destination,
			waypoints,
			vehicleId,
			customHeightM,
			fuelType,
			customMpg,
		}
		// A superseded run drops its result. It only clears the spinner when no
		// newer run owns it.
		const stillCurrent = (): boolean => {
			if (isCalcCurrent(generation, inputs, get())) return true
			if (generation === calcGeneration) set({ isCalculating: false })
			return false
		}
		set({ isCalculating: true, error: null })

		try {
			// Full pipeline — runs fresh on every Calculate. We used to have a
			// shared Redis route-cache keyed by a client-computed hash that
			// made share-link clicks instant across users, but it was trivially
			// poisonable (client computes the hash, any unauthenticated caller
			// could POST arbitrary data under a precomputed key). Removed
			// 2026-04-18 after Codex flagged it Critical #2. Every other cache
			// tier (elevation Redis, fuel prices L1+L2, CDN-edge geocode) still
			// works, so a share-link click takes ~1-3s instead of instant —
			// acceptable trade-off for eliminating the attack surface.
			const vehicle = getVehicle(vehicleId)
			const baseMpg = customMpg ?? vehicle.baseMpg
			const estimate = await runFuelPipeline(
				origin,
				destination,
				waypoints,
				vehicle,
				baseMpg,
				fuelType,
				customHeightM,
			)
			if (!stillCurrent()) return
			set({
				estimate,
				routeGeometry: estimate.routeGeometry,
				isCalculating: false,
				lastCalculationMs: Math.round(performance.now() - startMs),
				wasCached: false,
			})

			// Record the completed trip for the Recent Routes chip row.
			addRecentRoute({ origin, destination, waypoints })

			trackCalculated(estimate, { vehicleId, fuelType, customMpg, waypoints })
		} catch (err) {
			if (!stillCurrent()) return
			set({
				error: err instanceof Error ? err.message : 'Calculation failed',
				isCalculating: false,
			})
		}
	},

	reset: () => {
		// Supersede any in-flight Calculate so its result can't land after the reset.
		calcGeneration++
		set({
			isCalculating: false,
			auxIdleHours: 0,
			origin: null,
			destination: null,
			waypoints: [],
			estimate: null,
			routeGeometry: null,
			error: null,
		})
	},
}))

/**
 * Determine which US state each sample point falls in via the offline polygon
 * lookup endpoint (`/api/states-at-points`).
 *
 * Fires ONE server request with ALL sample points — not N individual calls —
 * because the lookup is a local computation (point-in-polygon against bundled
 * US state boundaries). No upstream dependencies, no per-coord cost, and
 * every sample is tested individually so thin-state corridors like WV on I-68
 * between PA and MD are captured correctly.
 *
 * Returns:
 *   - `ok`: the entire state array was resolved (including legitimate `null`
 *     values for coords outside US land — e.g. coastal routes briefly over
 *     water — which we bucket as 'US' for fuel pricing purposes)
 *   - `unavailable`: the endpoint itself failed (network, malformed response)
 */
async function determineStates(
	points: Array<{ lng: number; lat: number }>,
): Promise<ApiResult<string[]>> {
	try {
		const res = await fetchWithTimeout('/api/states-at-points', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ coordinates: points }),
		})
		if (!res.ok) {
			return unavailable(`State detection service returned ${res.status}`)
		}
		const data = await res.json()
		const statesArray = data.states as Array<string | null> | undefined
		if (!Array.isArray(statesArray) || statesArray.length !== points.length) {
			return unavailable('State detection returned malformed data')
		}
		// Defensive: if EVERY coord came back null, something is off (polygon
		// data corrupt, or a pathological route entirely outside US land).
		// Treat as unavailable so the warnings banner surfaces it.
		if (statesArray.every((s) => s === null)) {
			return unavailable('State detection returned null for all coordinates')
		}
		// Coerce nulls to 'US' — matches the pre-refactor pipeline contract
		// (engine buckets 'US' under national-average pricing).
		return ok(statesArray.map((s) => s ?? 'US'))
	} catch (err) {
		const reason = err instanceof Error ? err.message : 'State detection unreachable'
		return unavailable(reason)
	}
}

/**
 * The generator/reefer add-on for the current estimate, or null when it's
 * off, there's no estimate yet, or the vehicle has no on-board power. Shared
 * by the cost summary and the PDF so both always show the same numbers.
 */
export function selectAuxPowerLine(
	state: Pick<RouteState, 'estimate' | 'vehicleId' | 'auxPowerOn' | 'customGph' | 'auxIdleHours'>,
): (GeneratorLine & { label: string }) | null {
	const aux = getVehicle(state.vehicleId).auxPower
	if (!state.estimate || !state.auxPowerOn || !aux) return null
	return {
		label: aux.label,
		...generatorLine(state.estimate, state.customGph ?? aux.gph, state.auxIdleHours),
	}
}
